import React, { useState } from "react";

import {
  addYears,
  differenceInDays,
  isAfter,
  parseISO,
  startOfDay,
} from "date-fns";
import partition from "lodash/partition";

import { Learner, LessonStatus } from "@hubcapp/types";
import LearnersTable from "./LearnersTable";

import { LearnersTableStateProviderProps } from "./types";

const LearnersTableStateProvider: React.FC<LearnersTableStateProviderProps> = ({
  learners,
  loading,
}) => {
  const [activeFilter, setActiveFilter] = useState(true);

  const getLearnerAddressString = (learner: Partial<Learner>) => {
    const addressSource = learner.pickupAddress || learner.address;
    if (!addressSource) {
      return "-";
    }
    return [addressSource?.line1, addressSource?.postcode]
      .filter(Boolean)
      .join(", ");
  };

  const now = new Date();

  const parsedLearners = learners?.length
    ? learners.map((learner: Learner & { approved?: boolean }) => ({
        id: learner.id,
        approved: learner.approved,
        name: `${learner.name.firstName} ${learner.name.lastName}`,
        allocationDate: learner.allocations
          ? new Date(learner.allocations[0].fromDate)
          : null,
        lessonType: learner.allocations
          ? learner.allocations[0].lessonType?.toString()
          : "",
        lastLesson: learner.lastLesson
          ? parseISO(learner.lastLesson.toString())
          : !learner.approved
          ? addYears(new Date(), 1)
          : null,
        pickupAddress: getLearnerAddressString(learner),
        sourcedBy: learner.sourcedBy,
        postcode:
          learner.pickupAddress?.postcode || learner.address?.postcode || "",
        hasFutureLesson: !!learner.lessons.find(
          l =>
            isAfter(new Date(l.startDateTime), now) &&
            l.status === LessonStatus.Active,
        ),
      }))
    : [];

  // Partition the learners into an active and inactive learner arrays
  // We should consider separate queries for these tabs and do this logic on the backend
  const [activeLearners, inactiveLearners] = partition(
    parsedLearners,
    ({ approved, lastLesson, allocationDate, hasFutureLesson, lessonType }) => {
      // If they are a new learner and pending approval then we should show them in the active list
      if (!approved) {
        return true;
      }

      // If they are unallocated, they cannot be active.
      if (!allocationDate) {
        return false;
      }

      // Motability learners are always considered active (AB#7405)
      if (lessonType === "Motability") {
        return true;
      }

      const today = startOfDay(new Date(Date.now()));

      const recentlyAllocated =
        differenceInDays(today, new Date(allocationDate)) < 14;

      // If the learner has any upcoming lessons with the instructor, they are considered active.
      if (recentlyAllocated || hasFutureLesson) {
        return true;
      }

      // At this point, the learner is approved, allocated more than 14 days ago, and has no future lessons.
      // If the learner is yet to take a lesson, they cannot be active.
      if (!lastLesson) {
        return false;
      }

      // If the learner's most recent lesson was in the last 14 days, they are considered active.
      return differenceInDays(today, new Date(lastLesson)) < 14;
    },
  );

  return (
    <LearnersTable
      loading={loading}
      learners={activeFilter ? activeLearners : inactiveLearners}
      toggleActiveFilter={() => setActiveFilter(!activeFilter)}
      activeFilter={activeFilter}
    />
  );
};

export default LearnersTableStateProvider;
